<template>
    <div class="custom-input-wrapper" :class="[classContainerAdd, { isNotrequired: !required }]">
        <!-- Label (Standard) -->
        <div v-if="(label || $slots['label-icon']) && !floatingLabel" class="ppv2-flex ppv2-items-center ppv2-gap-1">
            <slot name="label-icon"></slot>
            <label
                v-if="label"
                :class="customLabelClass ? customLabelClass : 'ppv2-text-black poppinsMedium'"
                :style="{ fontSize: normalizeFontSize(customSizeLabel, true) }"
            >
                {{ label }}<span class="text-purple" v-if="required">*</span>
            </label>
        </div>

        <!-- Input container -->
        <div
            class="input-container ppv2-relative"
            :class="[
                classInputAdd,
                computedState,
                { 'has-icon': showIcon || iconIA || type === 'password' },
                { 'has-floating-label': floatingLabel && label },
            ]"
        >
            <!-- Floating Label -->
            <label
                v-if="floatingLabel && label"
                class="floating-label ppv2-text-opacity-70 hover:ppv2-text-opacity-100 poppinsMedium"
                :style="[
                    { fontSize: normalizeFontSize(customSizeLabel || 10, true) },
                    floatingLabelBg ? { backgroundColor: floatingLabelBg } : {},
                ]"
            >
                {{ label }}<span class="text-purple" v-if="required">*</span>
            </label>

            <input
                :type="type === 'password' ? (showPassword ? 'text' : 'password') : type"
                :value="value"
                :min="min"
                :max="max"
                :step="step"
                :disabled="computedState === 'disabled'"
                :readonly="readonly"
                :placeholder="placeholder"
                :name="name"
                v-bind="$attrs"
                class="custom-input ppv2-text-black"
                :style="[{ fontSize: normalizeFontSize(customSizeLabel, true) }]"
                @input="handleInput"
                @focus="$emit('focus')"
                @blur="$emit('blur')"
                @keyup.enter="$emit('enter')"
            />

            <!-- Right Icon -->
            <div v-if="rightIcon || type === 'password'" class="ppv2-flex ppv2-items-center ppv2-justify-space-between">
                <!-- Toggle Password Visibility Icon -->
                <button
                    v-if="type === 'password'"
                    type="button"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded-md ppv2-transition-colors"
                    @click.stop="togglePassword"
                >
                    <Eye v-if="!showPassword" :size="18" class="ppv2-text-[#94a3b8]" />
                    <EyeOff v-else :size="18" class="ppv2-text-[#94a3b8]" />
                </button>

                <!-- Right Icon -->
                <span v-if="rightIcon" class="icon rightIcon" :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }">
                    {{ rightIcon }}
                </span>
                <!-- ✅ Valid Icon -->
                <!--span
                    v-if="computedState === 'valid' && !hideValidIcon"
                    class="icon valid"
                    :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >✔</span
            -->

                <!-- ⚠️ Error Icon -->
                <span
                    v-if="computedState === 'error'"
                    class="icon error"
                    :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >!</span
                >
            </div>
            <div v-else>
                <!-- ✅ Valid Icon -->
                <!--span
                    v-if="computedState === 'valid' && !hideValidIcon"
                    class="icon valid"
                    :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >✔</span
            -->

                <!-- ⚠️ Error Icon -->
                <span
                    v-if="computedState === 'error'"
                    class="icon error"
                    :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >!</span
                >
            </div>

            <!-- ✨ IA Generate Icon -->
            <span v-if="iconIA" class="icon ia"
                ><img src="/media/bo/icons/ai/ai.svg" v-if="!isDark" /><img src="/media/svg/aiDark.svg" v-if="isDark"
            /></span>
        </div>

        <!-- Texte d’erreur -->
        <p v-if="errorText && computedState === 'error'" class="error-text" :class="{ '!ppv2-mb-0': isNoMarginBottom }">
            {{ Array.isArray(errorText) ? errorText[0] : errorText }}
        </p>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import { Eye, EyeOff } from 'lucide-vue'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'CustomInput',
    components: {
        Eye,
        EyeOff,
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handleInput(event) {
            let value = event.target.value

            if (this.digitsOnly) {
                value = value.replace(/\D/g, '')
                event.target.value = value
            }

            // Validation pour les champs de type number
            if (this.type === 'number' && value !== '') {
                const numValue = parseFloat(value)

                // Vérifier si c'est un nombre valide
                if (!isNaN(numValue)) {
                    // Vérifier la valeur minimale
                    if (this.min !== undefined && numValue < parseFloat(this.min)) {
                        value = this.min
                        event.target.value = this.min
                    }
                    // Vérifier la valeur maximale
                    else if (this.max !== undefined && numValue > parseFloat(this.max)) {
                        value = this.max
                        event.target.value = this.max
                    }
                }
            }

            this.$emit('input', value)
        },
        togglePassword() {
            this.showPassword = !this.showPassword
        },
    },
    props: {
        label: String,
        value: [String, Number],
        placeholder: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        rightIcon: {
            type: String,
            default: '',
        },
        state: {
            type: String,
            default: 'default', // 'default', 'disabled', 'active', 'valid', 'error'
        },
        customLabelClass: {
            type: String,
            default: '',
        },
        customSizeLabel: {
            type: Number,
            default: 15,
        },
        classInputAdd: {
            type: String,
            default: '',
        },
        classContainerAdd: {
            type: String,
            default: '',
        },
        fontSizeIconRight: {
            type: Number,
            default: 15,
        },
        errorText: [String, Array],
        required: Boolean,
        showIcon: Boolean,
        iconIA: Boolean,
        hideValidIcon: {
            type: Boolean,
            default: true,
        },
        step: {
            type: [String, Number],
            default: 'any',
        },
        min: {
            type: [String, Number],
            default: undefined,
        },
        disabled: Boolean,
        readonly: Boolean,
        max: {
            type: [String, Number],
            default: undefined,
        },
        name: {
            type: String,
            default: '',
        },
        floatingLabel: {
            type: Boolean,
            default: false,
        },
        floatingLabelBg: {
            type: String,
            default: '',
        },
        customHeight: {
            type: [Number, String],
            default: null,
        },
        digitsOnly: Boolean,
    },
    data() {
        return {
            showPassword: false,
        }
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        computedState() {
            if (this.disabled || this.state === 'disabled') {
                return 'disabled'
            }
            if (this.state === 'default' && this.errorText) {
                if (Array.isArray(this.errorText) && this.errorText.length > 0) {
                    return 'error'
                }
                if (typeof this.errorText === 'string' && this.errorText.trim() !== '') {
                    return 'error'
                }
            }
            return this.state
        },
        isNoMarginBottom() {
            return (
                this.classContainerAdd &&
                (this.classContainerAdd.includes('ppv2-mb-0') || this.classContainerAdd.includes('mb-0'))
            )
        },
    },
    watch: {},
}
</script>

<style scoped>
.custom-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-bottom: 20px;
    opacity: 0.7;
}

.custom-input-wrapper:hover,
.custom-input-wrapper:focus-within {
    opacity: 1;
}

.input-container {
    display: flex;
    align-items: center;
    border-radius: 12px;
    border: 1px solid #9c9af880;
    padding: 5.5px 10px;
    transition: all 0.2s ease;
    height: 40px;
}

.input-container.has-floating-label {
    margin-top: 8px;
}

.floating-label {
    position: absolute;
    top: 0;
    transform: translateY(-65%);
    left: 12px;
    padding: 0 4px;
    z-index: 10;
    background-color: #fff;
    pointer-events: none;
    line-height: 1;
    opacity: 1 !important;
    white-space: nowrap;
}
.dark .floating-label {
    background-color: #060b28; /* Solid background for dark mode to cut the border */
}

.input-container input.custom-input {
    width: 100%;
    /*background: transparent;*/
    border: none;
    outline: none;
}

.input-container.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.input-container.active {
    border-color: #9c9af8b2;
}

/*.input-container.valid {
    border-color: #31d0aa;
}*/

.input-container.error {
    border-color: #ed4b9e !important;
    background-color: initial !important;
}

.icon {
    margin-left: 4px;
    background-color: transparent;
}

/*.icon.valid {
    color: #31d0aa;
}*/

.icon.rightIcon {
    color: #94a3b8;
}

.icon.error {
    display: inline-block;
    color: #ed4b9e;
    background-color: initial !important;
    border: 1px solid;
    border-radius: 50%;
    width: 17px;
    height: 17px;
    text-align: center;
    line-height: 17px;
    font-size: 15px;
}

.icon.ia {
    color: #7c00f080;
}

.dark .icon.ia {
    color: #9c9af880;
}

.error-text {
    color: #ed4b9e;
    font-size: clamp(10px, 0.625vw, 22px);
}

/* Chrome, Safari, Edge */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
}

/* Firefox */
input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
